import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  IconButton,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { Autorenew } from '@mui/icons-material';
import Select from '../../components/Select';
import { IPromoCodeType, Promocode } from '../../resources/types/balanceTypes';
import { createOrUpdatePromocodeAsync } from '../../redux/slices/balanceSlice';
import { RootState } from '../../redux/store';
import { useEffect, useMemo, useCallback } from 'react';
import { SelectOption } from '../../components/Select/interface';
import { useFormik } from 'formik';
import DatePicker from '../../components/DatePicker/DatePicker';
import moment, { Moment } from 'moment';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { useTranslation } from 'react-i18next';

interface IPromocodeModalProps {
  selectedPromocode: Promocode | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  setSelectedPromocode: (promocode: Promocode) => void;
  onSuccess?: () => void;
}

const PromocodeModal = ({
  open,
  setOpen,
  selectedPromocode,
  onSuccess,
}: IPromocodeModalProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isSuperAdmin = useSelector(
    (state: RootState) => state.auth.info?.type === 'superAdmin'
  );
  const cities = useSelector((state: RootState) => state.cities.items);
  const adminInfo = useSelector(selectAdmins);

  const currentCityId = useMemo(() => {
    const city = cities.find(item => item._id === adminInfo.currentCity);
    if (city) {
      return city?._id;
    }
    return adminInfo?.currentCity;
  }, [adminInfo.currentCity, cities]);

  const citiesOptions = useMemo<SelectOption[]>(
    () =>
      cities.map(city => ({
        value: city._id,
        label: city.cityName
      })),
    [cities]
  );

  const generateCode = useCallback(() => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }, []);

  const { handleChange, handleSubmit, setFieldValue, setValues, values, resetForm } = useFormik({
    initialValues: {
      code: '',
      type: IPromoCodeType.addBalance,
      amount: 0,
      expiresAt: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
      description: '',
      usingLimit: 0,
      city: currentCityId,
      active: true,
      scooterName: '',
      durationMinutes: 0,
    },
    onSubmit: values => {
      const submitData: any = { ...values };
      if (values.type !== IPromoCodeType.freeRideSpecificScooter) {
        delete submitData.scooterName;
        delete submitData.durationMinutes;
      }
      dispatch(
        createOrUpdatePromocodeAsync(
          submitData,
          () => {
            setOpen(false);
            onSuccess?.();
          }
        )
      );

    }
  });

  useEffect(() => {
    if (open && selectedPromocode?._id) {
      setValues({
        code: selectedPromocode.code ?? '',
        type: selectedPromocode.type ?? IPromoCodeType.addBalance,
        amount: selectedPromocode.amount ?? 0,
        expiresAt: selectedPromocode.expiresAt ?? moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
        description: selectedPromocode.description ?? '',
        usingLimit: selectedPromocode.usingLimit ?? 0,
        city: selectedPromocode.city ?? currentCityId,
        active: selectedPromocode.active ?? true,
        scooterName: selectedPromocode.scooterName ?? '',
        durationMinutes: selectedPromocode.durationMinutes ?? 0,
      })
    } else if (open && !selectedPromocode?._id) {
      setFieldValue('code', generateCode());
    }

    if (!open) {
      resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <Dialog fullWidth maxWidth='sm' open={open} onClose={() => setOpen(false)}>
      <DialogTitle>
        {selectedPromocode?._id
          ? t('promocodes.editTitle')
          : t('promocodes.createTitle')}
      </DialogTitle>
      <DialogContent>
        {isSuperAdmin && (
          <Grid item marginBottom={1}>
            <Select
              fullWidth
              options={citiesOptions}
              onChange={value => setFieldValue('city', value)}
              value={values.city}
              label={t('promocodes.labelCity')}
            />
          </Grid>
        )}
        <Grid item marginBottom={1}>
          <TextField
            fullWidth
            label={t('promocodes.labelCode')}
            name='code'
            onChange={handleChange}
            value={values.code}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={t('promocodes.generateCodeTooltip')}>
                    <IconButton
                      onClick={() => setFieldValue('code', generateCode())}
                      edge="end"
                      size="small"
                      sx={{ color: '#4ECDC4' }}
                    >
                      <Autorenew />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item marginBottom={1}>
          <TextField
            fullWidth
            label={t('promocodes.labelDescription')}
            name='description'
            onChange={handleChange}
            value={values.description}
          />
        </Grid>
        <Grid item marginBottom={1}>
          <Select
            fullWidth
            options={[
              { value: IPromoCodeType.addBalance, label: t('promocodes.typeAddBalance') },
              { value: IPromoCodeType.subscription, label: t('promocodes.typeSubscription') },
              { value: IPromoCodeType.referal, label: t('promocodes.optReferal') },
              { value: IPromoCodeType.fortuneWheel, label: t('promocodes.optFortuneWheel') },
              { value: IPromoCodeType.freeRideSpecificScooter, label: t('promocodes.optFreeRide') },
            ]}
            onChange={value => setFieldValue('type', value)}
            value={values.type}
            label={t('promocodes.labelType')}
          />
        </Grid>
        {values.type === IPromoCodeType.freeRideSpecificScooter ? (
          <>
            <Grid item marginBottom={1}>
              <TextField
                fullWidth
                label={t('promocodes.labelScooterName')}
                name='scooterName'
                onChange={handleChange}
                value={values.scooterName}
                helperText={t('promocodes.helperScooterName')}
              />
            </Grid>
            <Grid item marginBottom={1}>
              <TextField
                fullWidth
                label={t('promocodes.labelDuration')}
                name='durationMinutes'
                type='number'
                onChange={handleChange}
                value={values.durationMinutes}
                helperText={t('promocodes.helperDuration')}
              />
            </Grid>
          </>
        ) : (
          <Grid item marginBottom={1}>
            <TextField
              fullWidth
              label={t('promocodes.labelAmount')}
              name='amount'
              type='number'
              onChange={handleChange}
              value={values.amount}
            />
          </Grid>
        )}
        <Grid item marginBottom={1}>
          <TextField
            fullWidth
            label={t('promocodes.labelUsingLimit')}
            name='usingLimit'
            type='number'
            onChange={handleChange}
            value={values.usingLimit}
          />
        </Grid>
        <Grid item marginBottom={1}>
        <DatePicker 
          label={t('promocodes.labelExpiresAt')} 
          value={values.expiresAt as unknown as Moment} 
          onChange={(date: Moment | null) => {
            console.log(date);
            setFieldValue('expiresAt', moment(date).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"))
          }} 
        />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={() => handleSubmit()}>
          {t('promocodes.saveBtn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromocodeModal;
